import baseApi from 'shared/api/baseApi';

import type { IdentityChangePreview } from '../types/identityChangePreview';
import {
  AddIdentityProjectResponse,
  FullUserBody,
  IdentityResponse,
  RoleWithPermissions,
  UpdateIdentityBody,
  UserDetail,
  UserFullResponse,
  UserListItem,
} from '../types/user';

export interface UsersListingParams {
  projectId?: number;
  onlyAdmins?: boolean;
  search?: string;
}

interface UsersListingResponse {
  users: UserListItem[];
}

interface AssignableRolesResponse {
  roles: RoleWithPermissions[] | BulkAssignableRole[];
}

export interface AssignableRolesParams {
  withPermissions?: boolean;
}

export interface BulkImportUsersRowError {
  row: number;
  nombre: string;
  email: string;
  errors: string[];
}

export interface BulkImportCreatedUserRow {
  nombre: string;
  email: string;
  phoneNumber: string | null;
  externalId: string | null;
  isAssignable: boolean;
  temporaryPassword: string;
  rolesDisplay: string;
  subProjectsDisplay: string;
}

export interface BulkImportUsersSuccessResponse {
  status: 'ok';
  message: string;
  created: number;
  failed: number;
  createdUsers: BulkImportCreatedUserRow[];
  errors: BulkImportUsersRowError[];
}

export interface BulkImportUsersValidationResponse {
  message: string;
  errorType: 'validation_error';
  errors: BulkImportUsersRowError[];
}

export type BulkImportUsersErrorResponse = BulkImportUsersValidationResponse | { message: string; errorType: string };

export interface BulkAssignableRole {
  id: number;
  name: string;
  displayName: string;
}

export interface BulkImportUserAssignment {
  email: string;
  roleIds: number[];
  subProjectIds: number[];
}

export interface BulkImportUsersRequest {
  projectId: number;
  usersFile: File;
  assignments: BulkImportUserAssignment[];
}

export interface IdentityOccupancyProject {
  id: number | null;
  name: string;
  isActive: boolean;
}

export interface IdentityOccupancyEmail {
  email: string;
  identityExists: boolean;
  occupiedOnCurrentProject: boolean;
  projects: IdentityOccupancyProject[];
}

export interface IdentityOccupancyRequest {
  projectId: number;
  emails: string[];
}

export interface IdentityOccupancyResponse {
  emails: IdentityOccupancyEmail[];
}

const usersApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getAssignableRoles: build.query<AssignableRolesResponse, AssignableRolesParams | void>({
      query: (params) => ({
        url: '/backoffice/users/assignable-roles',
        method: 'GET',
        ...(params?.withPermissions ? { params: { withPermissions: 'true' } } : {}),
      }),
      providesTags: ['Role'],
    }),
    getUsersListing: build.query<UsersListingResponse, UsersListingParams>({
      query: ({ projectId, onlyAdmins, search }) => ({
        url: '/backoffice/users/listing',
        method: 'GET',
        params: {
          ...(onlyAdmins ? { onlyAdmins: true } : { projectId }),
          ...(search ? { search } : {}),
        },
      }),
      providesTags: ['User'],
    }),
    getIdentityOccupancy: build.query<IdentityOccupancyResponse, IdentityOccupancyRequest>({
      query: ({ projectId, emails }) => ({
        url: '/backoffice/users/identity-occupancy',
        method: 'POST',
        body: { projectId, emails },
      }),
    }),
    getUserFull: build.query<UserFullResponse, number>({
      query: (userId) => ({
        url: `/admin/users/${userId}/full`,
        method: 'GET',
      }),
      providesTags: (_result, _error, userId) => [{ type: 'User', id: userId }],
    }),
    createUserFull: build.mutation<UserFullResponse, FullUserBody>({
      query: (body) => ({
        url: '/admin/users/full',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['User'],
    }),
    updateUserFull: build.mutation<UserFullResponse, { userId: number; body: Partial<FullUserBody> }>({
      query: ({ userId, body }) => ({
        url: `/admin/users/${userId}/full`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_r, _e, { userId }) => ['User', { type: 'User', id: userId }],
    }),
    updateIdentity: build.mutation<IdentityResponse, { identityId: number; body: UpdateIdentityBody }>({
      query: ({ identityId, body }) => ({
        url: `/admin/identities/${identityId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['User'],
    }),
    addIdentityProject: build.mutation<AddIdentityProjectResponse, { identityId: number; projectId: number }>({
      query: ({ identityId, projectId }) => ({
        url: `/admin/identities/${identityId}/projects`,
        method: 'POST',
        body: { projectId },
      }),
      invalidatesTags: ['User'],
    }),
    previewIdentityChange: build.mutation<IdentityChangePreview, { userId: number; email?: string }>({
      query: ({ userId, email }) => ({
        url: `/admin/users/${userId}/identity-change-preview`,
        method: 'POST',
        body: { email },
      }),
    }),
    bulkImportUsers: build.mutation<BulkImportUsersSuccessResponse, BulkImportUsersRequest>({
      query: ({ projectId, usersFile, assignments }) => {
        const body = new FormData();
        body.append('projectId', String(projectId));
        body.append('usersFile', usersFile);
        body.append('assignments', JSON.stringify(assignments));
        return {
          url: '/backoffice/users/bulk-import',
          method: 'POST',
          body,
          formData: true,
        };
      },
      invalidatesTags: ['User'],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetUsersListingQuery,
  useGetIdentityOccupancyQuery,
  useGetUserFullQuery,
  useCreateUserFullMutation,
  useUpdateUserFullMutation,
  useUpdateIdentityMutation,
  useAddIdentityProjectMutation,
  useGetAssignableRolesQuery,
  useBulkImportUsersMutation,
  usePreviewIdentityChangeMutation,
} = usersApi;

// Type re-export to consumers that don't need to know about RTK Query types.
export type { UserDetail, UserFullResponse, RoleWithPermissions, FullUserBody };

export default usersApi;
